import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Мост к Telegram WebApp SDK.
 *
 * Приложение обязано работать и вне Telegram (веб-версия, D-09), поэтому все
 * методы здесь — безопасные no-op при отсутствии SDK, а `isTMA` — единственный
 * признак, по которому UI меняет поведение.
 */

export type HapticKind =
  | 'light'
  | 'medium'
  | 'heavy'
  | 'rigid'
  | 'soft'
  | 'success'
  | 'warning'
  | 'error'
  | 'selection';

export interface MainButtonApi {
  setText(text: string): void;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  onClick(cb: () => void): () => void;
}

export interface TelegramApi {
  webApp: TelegramWebApp | null;
  isTMA: boolean;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  startParam: string | null;
  initData: string;
  haptic(kind?: HapticKind): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  openLink(url: string): void;
  openTelegramLink(url: string): void;
  mainButton: MainButtonApi;
}

const EMPTY_THEME: TelegramThemeParams = {};

export function getWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

/**
 * Признак запуска внутри Telegram. Проверяем именно `initData`: объект
 * WebApp существует и при открытии страницы в обычном браузере (скрипт SDK
 * подключён всегда), но initData там пустой.
 */
export function detectIsTMA(): boolean {
  const webApp = getWebApp();
  if (!webApp) return false;
  return typeof webApp.initData === 'string' && webApp.initData.length > 0;
}

/** ready()/expand() должны выполниться один раз за жизнь документа. */
let bootstrapped = false;

function bootstrapWebApp(webApp: TelegramWebApp): void {
  if (bootstrapped) return;
  bootstrapped = true;
  webApp.ready();
  webApp.expand();
  // Свайп вниз в TMA закрывает приложение и ломает вертикальные списки.
  // Метод появился в 7.7 — вызываем только если он есть.
  webApp.disableVerticalSwipes?.();
}

export function useTelegram(): TelegramApi {
  const webApp = getWebApp();
  const isTMA = detectIsTMA();
  const webAppRef = useRef<TelegramWebApp | null>(webApp);
  webAppRef.current = webApp;

  useEffect(() => {
    const app = webAppRef.current;
    if (app && isTMA) bootstrapWebApp(app);
  }, [isTMA]);

  const haptic = useCallback((kind: HapticKind = 'light') => {
    const feedback = webAppRef.current?.HapticFeedback;
    if (!feedback) return;
    try {
      if (kind === 'selection') feedback.selectionChanged();
      else if (kind === 'success' || kind === 'warning' || kind === 'error') {
        feedback.notificationOccurred(kind);
      } else feedback.impactOccurred(kind);
    } catch {
      // Старые клиенты бросают на неизвестном типе — это украшение, не функция.
    }
  }, []);

  const expand = useCallback(() => {
    webAppRef.current?.expand();
  }, []);

  const close = useCallback(() => {
    webAppRef.current?.close();
  }, []);

  const setHeaderColor = useCallback((color: string) => {
    const app = webAppRef.current;
    if (!app) return;
    const isHex = color.startsWith('#');
    // Hex-цвет заголовка поддерживается с 6.9; для старых клиентов только
    // ключи темы, иначе SDK бросает исключение.
    if (isHex && !app.isVersionAtLeast('6.9')) return;
    try {
      app.setHeaderColor(color);
    } catch {
      /* no-op */
    }
  }, []);

  const setBackgroundColor = useCallback((color: string) => {
    try {
      webAppRef.current?.setBackgroundColor(color);
    } catch {
      /* no-op */
    }
  }, []);

  const openLink = useCallback((url: string) => {
    const app = webAppRef.current;
    if (app) app.openLink(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const openTelegramLink = useCallback((url: string) => {
    const app = webAppRef.current;
    if (app) app.openTelegramLink(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const mainButton = useMemo<MainButtonApi>(
    () => ({
      setText: (text) => webAppRef.current?.MainButton.setText(text),
      show: () => webAppRef.current?.MainButton.show(),
      hide: () => webAppRef.current?.MainButton.hide(),
      enable: () => webAppRef.current?.MainButton.enable(),
      disable: () => webAppRef.current?.MainButton.disable(),
      showProgress: (leaveActive) => webAppRef.current?.MainButton.showProgress(leaveActive),
      hideProgress: () => webAppRef.current?.MainButton.hideProgress(),
      onClick: (cb) => {
        const button = webAppRef.current?.MainButton;
        if (!button) return () => undefined;
        button.onClick(cb);
        return () => button.offClick(cb);
      },
    }),
    [],
  );

  return {
    webApp,
    isTMA,
    platform: webApp?.platform ?? 'web',
    colorScheme: webApp?.colorScheme ?? 'dark',
    themeParams: webApp?.themeParams ?? EMPTY_THEME,
    startParam: webApp?.initDataUnsafe.start_param ?? null,
    initData: webApp?.initData ?? '',
    haptic,
    expand,
    close,
    setHeaderColor,
    setBackgroundColor,
    openLink,
    openTelegramLink,
    mainButton,
  };
}
